/**
 * WhatsAppWidget — Floating WhatsApp contact button (localized, 16 languages)
 * Positioned bottom-right, uses centralized BRAND config for phone number
 */
import { waHref } from '@/lib/whatsappPrefill';
import { useState, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import BRAND from '@/config/brand';
import { trackWhatsAppClick } from '@/lib/analytics';
import { Link } from 'react-router-dom';
import { chatRequestHref, CHAT_REQUEST_LABEL } from '@/lib/chatRequestLink';

const WA: Record<string, { title: string; desc: string; cta: string; }> = {
  en: { title: 'Chat with us', desc: 'Tell us about your project on WhatsApp.', cta: 'Open WhatsApp' },
  fr: { title: 'Discutez avec nous', desc: 'Parlez-nous de votre projet sur WhatsApp.', cta: 'Ouvrir WhatsApp' },
  es: { title: 'Chatea con nosotros', desc: 'Cuéntenos su proyecto por WhatsApp.', cta: 'Abrir WhatsApp' },
  ar: { title: 'تحدث معنا', desc: 'حدّثنا عن مشروعك عبر واتساب.', cta: 'فتح واتساب' },
  de: { title: 'Chatten Sie mit uns', desc: 'Erzählen Sie uns per WhatsApp von Ihrem Projekt.', cta: 'WhatsApp öffnen' },
  it: { title: 'Chatta con noi', desc: 'Raccontaci il tuo progetto su WhatsApp.', cta: 'Apri WhatsApp' },
  pt: { title: 'Fale conosco', desc: 'Fale-nos do seu projeto pelo WhatsApp.', cta: 'Abrir WhatsApp' },
  nl: { title: 'Chat met ons', desc: 'Vertel ons via WhatsApp over uw project.', cta: 'WhatsApp openen' },
  pl: { title: 'Porozmawiaj z nami', desc: 'Opowiedz nam o swoim projekcie na WhatsAppie.', cta: 'Otwórz WhatsApp' },
  tr: { title: 'Bizimle sohbet edin', desc: 'Projenizi bize WhatsApp üzerinden anlatın.', cta: 'WhatsApp\'ı aç' },
  ru: { title: 'Напишите нам', desc: 'Расскажите нам о своём проекте в WhatsApp.', cta: 'Открыть WhatsApp' },
  zh: { title: '联系我们', desc: '通过WhatsApp告诉我们您的项目需求。', cta: '打开WhatsApp' },
  ja: { title: 'チャットする', desc: 'WhatsAppでプロジェクトについてお聞かせください。', cta: 'WhatsAppを開く' },
  ko: { title: '채팅하기', desc: 'WhatsApp으로 프로젝트에 대해 알려 주세요.', cta: 'WhatsApp 열기' },
  sv: { title: 'Chatta med oss', desc: 'Berätta om ert projekt för oss på WhatsApp.', cta: 'Öppna WhatsApp' },
  da: { title: 'Chat med os', desc: 'Fortæl os om jeres projekt på WhatsApp.', cta: 'Åbn WhatsApp' },
};

export const WhatsAppWidget = forwardRef<HTMLDivElement, object>(function WhatsAppWidget(_, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const { language } = useLanguage();
  // QUICK-SUMMARY-1810c: compact, lower bubble on the quote widget
  const compact = /\/start(\/|$)/.test(useLocation().pathname);

  const t = WA[language] || WA.en;
  const { pathname } = useLocation();
  const requestHref = chatRequestHref(language, pathname);
  const requestLabel = CHAT_REQUEST_LABEL[language] || CHAT_REQUEST_LABEL.en;
  const waNumber = BRAND.phone.whatsapp.replace(/[\s+]/g, '');
  const waLink = waHref(waNumber, language, pathname);

  return (
    <div ref={ref} className={compact ? "cmp-safe cmp-keep fixed bottom-3 right-2 z-40 scale-75 origin-bottom-right" : "cmp-safe cmp-keep fixed bottom-24 md:bottom-16 right-4 z-40"}>
      {isOpen && (
        <div className="absolute bottom-16 right-0 bg-card rounded-xl shadow-xl border border-border p-4 w-72 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-foreground">{t.title}</h3>
            <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">{t.desc}</p>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick()}
            className="block w-full bg-[#25D366] text-white font-semibold py-2.5 rounded-lg text-center hover:bg-[#22c55e] transition-colors"
          >
            {t.cta}
          </a>
          <Link
            to={requestHref}
            onClick={() => setIsOpen(false)}
            data-cta-id="chat-widget-request"
            className="mt-2 block w-full border border-border text-foreground font-semibold py-2.5 rounded-lg text-center hover:bg-muted transition-colors"
          >
            {requestLabel}
          </Link>
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-[#25D366] hover:bg-[#22c55e] text-white rounded-full p-3.5 shadow-lg transition-all duration-300 hover:scale-110 flex items-center justify-center"
        aria-label={t.title}
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
});

WhatsAppWidget.displayName = 'WhatsAppWidget';
